/**
 * Shared Utilities - Moon Moon Agency
 * Essential utilities and helpers shared across all modules
 */

/*********************************************************************************************
 * 🌐 GLOBAL SETTINGS
 */
// Development mode detection (lazy evaluation)
Object.defineProperty(window, 'isDev', {
	get() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
});

// Breakpoints
window.MOBILE_BREAKPOINT = 768;
window.TABLET_SM_BREAKPOINT = 900;
window.TABLET_BREAKPOINT = 1024;

/*********************************************************************************************
 * 🛠️ SHARED UTILITIES
 */
// Simple logging utility
window.log = (level, module, message, data = {}) => {
	if (!window.isDev && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		const timestamp = window.getTimestamp?.() || new Date().toLocaleString();
		logFn(`${module} ${message}`, { timestamp, ...data });
	}
};

// Simple event emitter
window.emit = (event, detail = {}) => {
	return document.dispatchEvent(new CustomEvent(event, {
		detail: { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...detail },
		bubbles: true
	}));
};

// DOM utilities
window.getElement = (selector) => document.querySelector(selector);
window.getElements = (selector) => document.querySelectorAll(selector);

// Common utilities
/**
 * Global device detection
 * - window.isMobile() → <= 768px
 * - window.isTabletSm() → <= 900px
 * - window.isTablet() → <= 1024px
 */
window.isMobile = () => window.innerWidth <= window.MOBILE_BREAKPOINT;
window.isTabletSm = () => window.innerWidth <= window.TABLET_SM_BREAKPOINT;
window.isTablet = () => window.innerWidth <= window.TABLET_BREAKPOINT;
// Appareil tactile sans souris (tablette, telephone). Synchro CSS : meme condition
// dans moon-moon.css.liquid pour basculer .mm-desktop-1024 -> menu mobile/burger.
window.isTouchDevice = () => window.matchMedia('(hover: none) and (pointer: coarse)').matches;
window.debounce = (fn, delay) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn.apply(this, args), delay);
	};
};

/*********************************************************************************************
 * 🔒 SCROLL LOCK — gele le body en position fixed pour eviter le saut visuel
 * Reentrant : plusieurs overlays (menu, cart, search) peuvent verrouiller simultanement
 */
window._scrollLockCount = 0;
window._scrollLockY = 0;
window.lockScroll = (className = 'mm-scroll-locked') => {
	const html = document.documentElement;
	if (window._scrollLockCount === 0) {
		window._scrollLockY = window.scrollY || window.pageYOffset || 0;
		html.style.setProperty('--scroll-lock-offset', `-${window._scrollLockY}px`);
		html.classList.add(className);
	}
	window._scrollLockCount += 1;
};
window.unlockScroll = (className = 'mm-scroll-locked') => {
	const html = document.documentElement;
	if (window._scrollLockCount === 0) return;
	window._scrollLockCount -= 1;
	if (window._scrollLockCount === 0) {
		html.classList.remove(className);
		html.style.removeProperty('--scroll-lock-offset');
		// `behavior: 'instant'` OBLIGATOIRE : le theme applique `scroll-behavior: smooth`
		// sur <html>, donc un scrollTo par defaut declenche une animation. Comme le body
		// vient de repasser de `position: fixed` a statique, cette animation part d'une
		// position clampee et fait "sauter" la page (bug de scroll a la fermeture d'overlay).
		window.scrollTo({ top: window._scrollLockY, left: 0, behavior: 'instant' });
	}
};

/*********************************************************************************************
 * 🕐 TIMESTAMP UTILITIES
 */
// French timestamp formatter
window.getTimestamp = () => new Date().toLocaleString('fr-FR', {
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit'
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Share URL Select on Click (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('[data-share-url]').forEach((input) => {
		input.addEventListener('click', () => {
			input.select();
		});
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	const eventConfig = {
		// Navigation & Filter events
		'mobile-menu-toggled': '📱',

		// Search events
		'search-opened': '🔍',
		'search-closed': '🔍',

		// UI Components events
		'dropdown-toggled': '📖',
		'read-more-toggled': '📝',

		// Media events
		'video-manager-initialized': '🎥',

		// Recommendations events
		'product-recommendations-loaded': '🛍️'
	};

	Object.entries(eventConfig).forEach(([event, emoji]) => {
		document.addEventListener(`global:${event}`, e =>
			console.log(`${emoji} [${event.toUpperCase()}]:`, e.detail)
		);
	});
}